import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Checkbox,
    Colors,
    Icon,
    ModalDialog,
    SearchBar,
    Text,
} from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import type { EmployeeCompetency } from '@/types/manager-assessment/Competency';

import { useAmelioTranslations } from '@/hooks';

interface AddSkillsModalProps {
    isOpen: boolean;
    onClose: () => void;
    competencies: EmployeeCompetency[];
    selectedSkillIds: string[];
    onConfirm: (skillIds: string[]) => void;
}

export const AddSkillsModal = ({
    isOpen,
    onClose,
    competencies,
    selectedSkillIds,
    onConfirm,
}: AddSkillsModalProps) => {
    const { t } = useAmelioTranslations();
    const [search, setSearch] = useState('');
    const [newSelectedIds, setNewSelectedIds] = useState<Set<string>>(new Set());

    const groupedByCategory = useMemo(() => {
        const grouped = new Map<string, EmployeeCompetency[]>();
        competencies.forEach((competency) => {
            const cat = competency.groupName ?? t('managerAssessmentSetup.ungrouped');
            const existing = grouped.get(cat) ?? [];
            existing.push(competency);
            grouped.set(cat, existing);
        });
        return Array.from(grouped.entries());
    }, [competencies, t]);

    const filteredCategories = useMemo(() => {
        if (!search.trim()) return groupedByCategory;
        const lowerSearch = search.toLowerCase();
        return groupedByCategory
            .map(
                ([cat, skills]) =>
                    [
                        cat,
                        skills.filter((skill) => skill.name.toLowerCase().includes(lowerSearch)),
                    ] as [string, EmployeeCompetency[]],
            )
            .filter(([, skills]) => skills.length > 0);
    }, [groupedByCategory, search]);

    const handleToggleSkill = (skillId: string) => {
        if (selectedSkillIds.includes(skillId)) return;
        setNewSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(skillId)) {
                next.delete(skillId);
            } else {
                next.add(skillId);
            }
            return next;
        });
    };

    const handleConfirm = () => {
        onConfirm(Array.from(newSelectedIds));
        setNewSelectedIds(new Set());
        setSearch('');
        onClose();
    };

    const handleClose = () => {
        setNewSelectedIds(new Set());
        setSearch('');
        onClose();
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={handleClose}
            title={t('managerAssessmentSetup.addSkillsModal.title')}
            titleIcon={
                <Icon
                    name={'grid-element-1'}
                    size={24}
                    color={Colors.SECONDARY_400}
                    ariaHidden
                />
            }
            size={'md'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={'add-skills-modal'}
            headerExtra={
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder={t('managerAssessmentSetup.addSkillsModal.searchPlaceholder')}
                    ariaLabel={t('managerAssessmentSetup.addSkillsModal.searchPlaceholder')}
                    dataTestId={'add-skills-search'}
                />
            }
            footer={
                <div className={'flex flex-col items-center justify-between gap-2 sm:flex-row'}>
                    <Text
                        size={'small'}
                        weight={'medium'}
                        color={Colors.BASE_500}
                    >
                        {t('managerAssessmentSetup.addSkillsModal.selectedCount', {
                            count: newSelectedIds.size,
                        })}
                    </Text>

                    <div className={'flex gap-3'}>
                        <ButtonSecondary
                            size={'sm'}
                            onClick={handleClose}
                            dataTestId={'add-skills-cancel'}
                        >
                            {t('managerAssessmentSetup.addSkillsModal.cancel')}
                        </ButtonSecondary>

                        <ButtonGradientPrimary
                            size={'sm'}
                            onClick={handleConfirm}
                            disabled={newSelectedIds.size === 0}
                            dataTestId={'add-skills-confirm'}
                        >
                            {t('managerAssessmentSetup.addSkillsModal.addSelected')}
                        </ButtonGradientPrimary>
                    </div>
                </div>
            }
        >
            <div className={'flex flex-col gap-5'}>
                {filteredCategories.map(([category, skills]) => (
                    <section
                        key={category}
                        className={'flex flex-col gap-3'}
                    >
                        <Text
                            as={'h4'}
                            weight={'semibold'}
                            color={Colors.BASE_600}
                        >
                            {category}
                        </Text>

                        {skills.map((skill) => {
                            const alreadySelected = selectedSkillIds.includes(skill.id);
                            return (
                                <div
                                    key={skill.id}
                                    className={'flex items-center justify-between gap-3'}
                                >
                                    <Checkbox
                                        checked={newSelectedIds.has(skill.id) || alreadySelected}
                                        onChange={() => handleToggleSkill(skill.id)}
                                        disabled={alreadySelected}
                                        label={skill.name}
                                        dataTestId={`add-skill-${skill.id}`}
                                    />

                                    {alreadySelected && (
                                        <Text
                                            size={'small'}
                                            weight={'medium'}
                                            color={Colors.PRIMARY_800}
                                        >
                                            {t(
                                                'managerAssessmentSetup.addSkillsModal.alreadyAdded',
                                            )}
                                        </Text>
                                    )}
                                </div>
                            );
                        })}
                    </section>
                ))}

                {filteredCategories.length === 0 && (
                    <Text
                        align={'center'}
                        color={Colors.BASE_500}
                        className={'py-8'}
                    >
                        {t('managerAssessmentSetup.addSkillsModal.noSkills')}
                    </Text>
                )}
            </div>
        </ModalDialog>
    );
};
